import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useI18n } from "../i18n/I18nContext";
import NewsletterForm from "../components/NewsletterForm";

export default function Home() {
  const { t, dict } = useI18n();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      // Wait for DOM paint
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
      });
    }
  }, [location.hash, location.pathname]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section id="top" className="relative bg-[#0B2545] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-40">
          <img
            src="/images/hero-vaca-muerta.jpg"
            alt={t("hero.image_alt")}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B2545] via-[#0B2545]/85 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="inline-block text-[10px] font-semibold uppercase tracking-widest text-[#C85A32] border border-[#C85A32]/60 px-2 py-1 mb-6">
              {t("hero.eyebrow")}
            </div>
            <h1
              data-testid="hero-title"
              className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-white"
            >
              {t("hero.title")}
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/85 leading-relaxed">
              {t("hero.paragraph_1")}
            </p>
            <p className="mt-4 text-base md:text-lg text-white/85 leading-relaxed">
              {t("hero.paragraph_2")}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href="#newsletter"
                data-testid="hero-cta-primary"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("newsletter")?.scrollIntoView({ block: "start" });
                }}
                className="inline-flex items-center justify-center bg-[#C85A32] hover:bg-[#B04B27] text-white text-sm font-semibold uppercase tracking-wider px-6 py-3"
              >
                {t("hero.cta_primary")}
              </a>
              <a
                href="#process"
                data-testid="hero-cta-secondary"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("process")?.scrollIntoView({ block: "start" });
                }}
                className="inline-flex items-center justify-center border border-white/40 text-white text-sm font-semibold uppercase tracking-wider px-6 py-3 hover:bg-white/10"
              >
                {t("hero.cta_secondary")}
              </a>
            </div>

            {/* Stats */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-0 border-t border-white/15 divide-y sm:divide-y-0 sm:divide-x divide-white/15">
              {[1, 2, 3].map((i) => (
                <div key={i} className="py-4 sm:py-5 sm:px-5 first:sm:pl-0" data-testid={`hero-stat-${i}`}>
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-[#C85A32]">
                    {t(`hero.stat_${i}_label`)}
                  </div>
                  <div className="text-2xl font-bold text-white mt-1">
                    {t(`hero.stat_${i}_value`)}
                  </div>
                  <div className="text-xs text-white/60 mt-1">
                    {t(`hero.stat_${i}_hint`)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right visual card */}
          <div className="lg:col-span-5 lg:pl-4">
            <div className="bg-white text-[#0F172A] border border-white/10 shadow-lg">
              <div className="relative">
                <img
                  src="/images/lithium-salar.jpg"
                  alt={t("hero.image_alt")}
                  className="w-full h-56 md:h-64 object-cover"
                />
                <div className="absolute top-3 left-3 bg-[#0B2545] text-white text-[10px] font-mono px-2 py-1">
                  {t("hero.card_region")}
                </div>
              </div>
              <div className="p-5 border-t border-[#E2E8F0]">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-[#C85A32]">
                  {t("hero.card_eyebrow")}
                </div>
                <div className="text-sm font-semibold text-[#0F172A] mt-1">
                  {t("hero.card_title")}
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4 text-xs">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="border border-[#E2E8F0] p-3">
                      <div className="text-[10px] uppercase tracking-wider text-[#64748B]">
                        {t(`hero.card_metric_${i}_label`)}
                      </div>
                      <div className="text-lg font-bold text-[#0B2545]">
                        {t(`hero.card_metric_${i}_value`)}
                      </div>
                      <div className={`text-[11px] ${i === 2 ? "text-[#B45309]" : "text-[#15803D]"}`}>
                        {t(`hero.card_metric_${i}_hint`)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section id="benefits" className="bg-white py-16 md:py-20 border-b border-[#E2E8F0]" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-[#C85A32] mb-3">
              {t("benefits.eyebrow")}
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]">
              {t("benefits.title")}
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-0 border border-[#CBD5E1]" data-testid="benefits-grid-container">
            {dict.benefits.items.map((b, idx) => (
              <div
                key={idx}
                className={`p-6 md:p-8 ${idx % 2 === 0 ? "md:border-r border-[#CBD5E1]" : ""} ${idx < 2 ? "md:border-b border-[#CBD5E1]" : ""} border-b md:border-b`}
                data-testid={`benefit-item-${idx + 1}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="font-mono text-xs text-[#C85A32]">0{idx + 1}</span>
                  <span className="h-px flex-1 bg-[#CBD5E1]" />
                </div>
                <h3 className="text-lg md:text-xl font-semibold text-[#0B2545]">{b.title}</h3>
                <p className="mt-3 text-sm text-[#334155] leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="bg-[#F8FAFC] py-16 md:py-20 border-b border-[#E2E8F0]" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-[#C85A32] mb-3">
                {t("process.eyebrow")}
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]">
                {t("process.title")}
              </h2>
              <p className="mt-4 text-sm text-[#334155] leading-relaxed">{t("process.intro")}</p>

              <div className="mt-6 border border-[#CBD5E1] bg-white overflow-hidden">
                <img
                  src="/images/dashboard.jpg"
                  alt={t("transparency.image_alt")}
                  className="w-full h-40 object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-0 border-l border-t border-[#CBD5E1] bg-white">
              {dict.process.steps.map((s, idx) => (
                <div
                  key={idx}
                  className="p-6 md:p-8 border-r border-b border-[#CBD5E1]"
                  data-testid={`process-step-${idx + 1}`}
                >
                  <div className="flex items-baseline gap-3 mb-2">
                    <span className="text-3xl font-bold text-[#C85A32] font-mono">{s.number}</span>
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-[#64748B]">
                      {t("process.step_label")}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-[#0B2545]">{s.title}</h3>
                  <p className="mt-2 text-sm text-[#334155] leading-relaxed">{s.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section id="transparency" className="bg-white py-16 md:py-20 border-b border-[#E2E8F0]" data-testid="transparency-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-[#C85A32] mb-3">
              {t("transparency.eyebrow")}
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]">
              {t("transparency.title")}
            </h2>
            <p className="mt-5 text-base text-[#334155] leading-relaxed">
              {t("transparency.paragraph_1")}
            </p>
            <p className="mt-4 text-base text-[#334155] leading-relaxed">
              {t("transparency.paragraph_2")}
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-0 border border-[#CBD5E1]">
              {dict.transparency.badges.map((b, idx) => (
                <div
                  key={idx}
                  className={`p-4 ${idx % 2 === 0 ? "sm:border-r border-[#CBD5E1]" : ""} ${idx < 2 ? "sm:border-b border-[#CBD5E1]" : ""} border-b sm:border-b`}
                  data-testid={idx === 0 ? "transparency-cuit-badge" : `transparency-badge-${idx + 1}`}
                >
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-[#64748B]">
                    {b.label}
                  </div>
                  <div className="text-base font-mono font-semibold text-[#0B2545] mt-1">
                    {b.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-[#CBD5E1]">
              <img
                src="/images/macro-chart.jpg"
                alt={t("transparency.image_alt")}
                className="w-full h-64 md:h-80 object-cover"
              />
              <div className="p-4 border-t border-[#CBD5E1]">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-[#C85A32]">
                  {t("transparency.audit_label")}
                </div>
                <div className="text-sm text-[#334155] mt-1">
                  {t("transparency.audit_note")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section id="newsletter" className="bg-[#0B2545] py-16 md:py-20" data-testid="newsletter-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6 text-white">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-[#C85A32] mb-3">
              {t("newsletter.eyebrow")}
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
              {t("newsletter.title")}
            </h2>
            <p className="mt-5 text-base text-white/80 leading-relaxed">
              {t("newsletter.subtitle")}
            </p>

            <div className="mt-8 border border-white/20">
              <img
                src="/images/oil-rig.jpg"
                alt={t("hero.image_alt")}
                className="w-full h-48 object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-6">
            <NewsletterForm variant="full" testIdPrefix="newsletter-form" />
          </div>
        </div>
      </section>
    </main>
  );
}
